// Analyst-side escalation state banner for the
// CriteriaChecklistItem popup. Renders Pending / Approved / Denied based on
// the most recent EscalationRecord surfaced through item.activeEscalation
// on the list / single-item GET. Sourcing from the same DTO as the row chip
// means display names are pre-resolved (no raw GUIDs on screen).

import { Clock, Check, X } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { checklistApi } from "@/api/dealFlow/client";
import { useQueryClient } from "@tanstack/react-query";
import type { CriteriaChecklistItemDto } from "@/api/dealFlow/types";
import { useDateFormatter } from "@/utils/datetime";

interface Props {
  dealId: string;
  item: CriteriaChecklistItemDto;
}

export function EscalationStateBanner({ dealId, item }: Props) {
  const qc = useQueryClient();
  const { dateTime } = useDateFormatter();

  const reset = async () => {
    await checklistApi.resetToPending(dealId, item.id);
    qc.invalidateQueries({ queryKey: ["deal-flow"] });
  };

  if (item.status !== "Escalated") return null;
  const r = item.activeEscalation;
  if (!r) return null;

  if (r.status === "Pending") {
    return (
      <div
        role="status"
        className="flex flex-col gap-space-1 border-l-4 border-status-warning bg-white-warm p-space-3 rounded-md"
      >
        <div className="flex items-center gap-space-2 font-medium">
          <Clock size={16} aria-hidden />
          <span>Escalation Pending</span>
        </div>
        <div className="text-small">
          Awaiting executive review. You escalated this item on{" "}
          {dateTime(r.escalatedAt)}.
        </div>
        <div className="text-small">
          Your reason: <em>&quot;{r.escalationReason}&quot;</em>
        </div>
      </div>
    );
  }

  if (r.status === "Approved") {
    return (
      <div
        role="status"
        className="flex flex-col gap-space-1 border-l-4 border-status-success bg-white-warm p-space-3 rounded-md"
      >
        <div className="flex items-center gap-space-2 font-medium">
          <Check size={16} aria-hidden />
          <span>Escalation Approved</span>
        </div>
        <div className="text-small">
          Approved by <strong>{r.resolvedByUserName ?? "—"}</strong> on{" "}
          {r.resolvedAt ? dateTime(r.resolvedAt) : ""}.
        </div>
        <div className="text-small">
          <strong>Directive:</strong> {r.resolutionDirective}
        </div>
        {r.resolutionNotes && (
          <div className="text-small">
            <strong>Notes:</strong>{" "}
            <em>&quot;{r.resolutionNotes}&quot;</em>
          </div>
        )}
      </div>
    );
  }

  // Denied
  return (
    <div
      role="status"
      className="flex flex-col gap-space-2 border-l-4 border-status-error bg-white-warm p-space-3 rounded-md"
    >
      <div className="flex items-center gap-space-2 font-medium">
        <X size={16} aria-hidden />
        <span>Escalation Denied</span>
      </div>
      <div className="text-small">
        Denied by <strong>{r.resolvedByUserName ?? "—"}</strong> on{" "}
        {r.resolvedAt ? dateTime(r.resolvedAt) : ""}.
      </div>
      <div className="text-small">
        <strong>Directive:</strong> {r.resolutionDirective}
      </div>
      {r.resolutionNotes && (
        <div className="text-small">
          <strong>Notes:</strong>{" "}
          <em>&quot;{r.resolutionNotes}&quot;</em>
        </div>
      )}
      <div>
        <Button variant="secondary" size="sm" onClick={() => void reset()}>
          Return to Pending and rework
        </Button>
      </div>
    </div>
  );
}
